import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from './configurationSlice';

import type {
  ReasonCommentTemplate,
  ReasonCommentTemplatesResponse,
  UpsertReasonCommentDefaultRequest,
  UpsertReasonCommentOverrideRequest,
} from '@/apps/common/settings/application/comment_configurator/types';
import {
  MESSAGES,
  SCOPES,
  createNotificationHandler,
} from '@/redux/notificationHandler';
import { axiosBaseQuery } from '@/redux/axios';
import { apiRoutes } from '@/api/apiRoutes';

interface UpdateDefaultArgs {
  reasonCode: string;
  body: UpsertReasonCommentDefaultRequest;
}

interface UpsertOverrideArgs {
  reasonCode: string;
  workItemType: number;
  body: UpsertReasonCommentOverrideRequest;
}

interface DeleteOverrideArgs {
  reasonCode: string;
  workItemType: number;
}

// Replaces the matching reason in the cached list with the template returned by
// a mutation. Merges so a partial response never drops fields it omitted.
const patchCachedReason = (
  updated: ReasonCommentTemplate,
  dispatch: (action: unknown) => void
) =>
  dispatch(
    reasonCommentsApi.util.updateQueryData(
      'getReasonComments',
      undefined,
      (draft) => {
        const idx = draft.reasons.findIndex((r) => r.code === updated.code);
        if (idx !== -1) {
          draft.reasons[idx] = { ...draft.reasons[idx], ...updated };
        }
      }
    )
  );

export const reasonCommentsApi = createApi({
  reducerPath: 'reasonCommentsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ReasonComments'],
  endpoints: (builder) => ({
    getReasonComments: builder.query<ReasonCommentTemplatesResponse, void>({
      query: () => ({
        url: apiRoutes.getReasonComments.path(),
        method: apiRoutes.getReasonComments.method,
      }),
      providesTags: ['ReasonComments'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_COMMENT_CONFIGURATOR,
        MESSAGES.LOAD,
        false
      ),
    }),
    updateReasonCommentDefault: builder.mutation<
      ReasonCommentTemplate,
      UpdateDefaultArgs
    >({
      query: ({ reasonCode, body }) => ({
        url: apiRoutes.updateReasonCommentDefault.path(reasonCode),
        method: apiRoutes.updateReasonCommentDefault.method,
        data: body,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_COMMENT_CONFIGURATOR,
          MESSAGES.UPDATE
        )(args, { dispatch, queryFulfilled });

        const { data: updated } = await queryFulfilled;
        patchCachedReason(updated, dispatch);
      },
    }),
    upsertReasonCommentOverride: builder.mutation<
      ReasonCommentTemplate,
      UpsertOverrideArgs
    >({
      query: ({ reasonCode, workItemType, body }) => ({
        url: apiRoutes.upsertReasonCommentOverride.path(
          reasonCode,
          workItemType
        ),
        method: apiRoutes.upsertReasonCommentOverride.method,
        data: body,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_COMMENT_CONFIGURATOR,
          MESSAGES.UPDATE
        )(args, { dispatch, queryFulfilled });

        const { data: updated } = await queryFulfilled;
        patchCachedReason(updated, dispatch);
      },
    }),
    deleteReasonCommentOverride: builder.mutation<void, DeleteOverrideArgs>({
      query: ({ reasonCode, workItemType }) => ({
        url: apiRoutes.deleteReasonCommentOverride.path(
          reasonCode,
          workItemType
        ),
        method: apiRoutes.deleteReasonCommentOverride.method,
      }),
      async onQueryStarted(
        { reasonCode, workItemType },
        { dispatch, queryFulfilled }
      ) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_COMMENT_CONFIGURATOR,
          MESSAGES.DELETE
        )({ reasonCode, workItemType }, { dispatch, queryFulfilled });

        // DELETE returns 204 with no body, so update the cache by hand.
        await queryFulfilled;
        dispatch(
          reasonCommentsApi.util.updateQueryData(
            'getReasonComments',
            undefined,
            (draft) => {
              const reason = draft.reasons.find((r) => r.code === reasonCode);
              if (!reason?.overrides) {
                return;
              }
              reason.overrides = reason.overrides.filter(
                (o) => o.workItemType !== workItemType
              );
              if (typeof reason.overrideCount === 'number') {
                reason.overrideCount = reason.overrides.length;
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetReasonCommentsQuery,
  useUpdateReasonCommentDefaultMutation,
  useUpsertReasonCommentOverrideMutation,
  useDeleteReasonCommentOverrideMutation,
} = reasonCommentsApi;

export default reasonCommentsApi;
